import type { DashboardDto } from "@/lib/api/types";

import { builtinHmiSvgAssets } from "../registry/builtin-svg-assets";
import { builtinHmiSymbols } from "../registry/builtin-symbols";
import type { HmiCanvasDocument } from "../schema/hmi-canvas.schema";
import { fromApiShape } from "./default-document";
import { isHmiLayout, migrateHmiDocument } from "./document-migrations";

export function getDashboardLayoutKind(layout: DashboardDto["layout"]): "classic" | "hmi_canvas" {
  if (layout && typeof layout === "object" && "kind" in layout && layout.kind === "hmi_canvas") {
    return "hmi_canvas";
  }
  return "classic";
}

function mergeBuiltinHmiResources(document: HmiCanvasDocument): HmiCanvasDocument {
  const builtinSymbolIds = new Set(builtinHmiSymbols.map((symbol) => symbol.id));
  const builtinAssetIds = new Set(builtinHmiSvgAssets.map((asset) => asset.id));

  const mergedSymbols = [
    ...builtinHmiSymbols,
    ...document.symbols.filter((symbol) => !builtinSymbolIds.has(symbol.id)),
  ];
  const mergedAssets = [
    ...builtinHmiSvgAssets.map((asset) => ({ ...asset })),
    ...document.assets.filter((asset) => !builtinAssetIds.has(asset.id)),
  ];
  const revisions = document.metadata.revisions ?? [];
  const mergedRevisions =
    revisions.length > 0
      ? revisions.map((revision) => {
          const nestedDocument = revision?.document;
          const nestedLayout = nestedDocument as { kind?: unknown } | null;
          if (!nestedLayout || typeof nestedLayout !== "object" || nestedLayout.kind !== "hmi_canvas") {
            return revision;
          }
          return {
            ...revision,
            document: mergeBuiltinHmiResources(nestedDocument as HmiCanvasDocument),
          };
        })
      : revisions;

  return {
    ...document,
    symbols: mergedSymbols,
    assets: mergedAssets,
    metadata: {
      ...document.metadata,
      revisions: mergedRevisions,
    },
  };
}

export function readHmiLayout(layout: DashboardDto["layout"]): HmiCanvasDocument | null {
  if (!layout) {
    return null;
  }
  const normalized = fromApiShape(layout);
  if (!isHmiLayout(normalized)) {
    return null;
  }
  try {
    return mergeBuiltinHmiResources(migrateHmiDocument(normalized));
  } catch (error) {
    console.error("Could not parse HMI dashboard layout.", error);
    return null;
  }
}
